import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { useLocation, useNavigate } from 'react-router-dom';
import { CircularProgress, Container } from '@mui/material';
import { useAppDispatch, useAppSelector } from "../../Config/Hooks";
import { logout } from '../../Config/Reducers/AuthenticationReducer';
import { useWebSocket } from './hooks/useWebSocket';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { resolveHomeRoute } from '../../Utils/HomeScreenUtils';
import { useAuthToken } from '../../Config/Hooks/useAuthToken';

const Login: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar();

    const auth = useAuth();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { status: sessionStatus, isAuthenticated: sessionReady } = useAuthToken();

    const account = useAppSelector((state) => state.authentication.account);
    const loginError = useAppSelector(state => state.authentication.loginError);
    const errorMessage = useAppSelector(state => state.authentication.errorMessage);
    const tokenError = auth.error?.message?.includes("Invalid");

    const [customerName, setCustomerName] = useState<string | null>(null);
    const { sendMessage, hostname } = useWebSocket();

    const handleError = useCallback((msg: string) => {
        if(sendMessage && auth.user?.profile.preferred_username) {
            sendMessage({
                email: auth.user.profile.preferred_username,
                hostname: hostname,
                customerName: customerName,
                action: "login",
                message: msg,
                status: "ERROR",
                timeStamp: new Date().toISOString(),
            });
        }
    }, [sendMessage, hostname, customerName, auth.user?.profile.preferred_username]);

    const fetchCustomerName = useCallback(async (email: string) => {
        try {
            const response = await axios.get("/api/customer/find-by-email", {
                params: { email: email },
            });
            if(response?.status === 200){
                const customerName = response.data.data ? String(response.data.data) : null;
                setCustomerName(customerName);
            } else {
                setCustomerName(null);
            }
        } catch (error) {
            setCustomerName(null);
        }
    }, []);

    useEffect(() => {
        if (auth.isLoading) {
            return;
        }

        if (tokenError) {
            auth.signoutRedirect();
            return;
        }

        if (!auth.isAuthenticated && !tokenError) {
            const currentUrl = window.location.href;
            const hasCode = currentUrl.includes('code=');
            const hasError = currentUrl.includes('error=');

            if (!hasCode && !hasError) {
                auth.signinRedirect();
            } else if (auth.error?.message?.includes('No matching state found in storage')) {
                window.location.href = window.location.origin;
            }
            return;
        }

        if (auth.user?.profile.email) {
            setCustomerName(null);
            fetchCustomerName(auth.user.profile.email);
        }
    }, [auth.isLoading, auth.isAuthenticated, tokenError, auth.user?.profile.email, auth.error, fetchCustomerName]);

    useEffect(() => {
        if (!sessionReady || !account?.name) {
            return;
        }

        const from = (location.state as { from?: { pathname: string; search?: string; hash?: string } })?.from;
        if (from?.pathname && from.pathname !== '/') {
            navigate(`${from.pathname}${from.search ?? ''}${from.hash ?? ''}`, { replace: true });
            return;
        }

        navigate(resolveHomeRoute(account.homeScreen, account.authoritySet), { replace: true });
    }, [sessionReady, account, location.state, navigate]);

    useEffect(() => {
        if (!loginError && !tokenError) return;

        let msg = "";
        if (errorMessage?.toLowerCase().includes("timeout") || errorMessage?.toLowerCase().includes("network")) {
            msg = "Falha de comunicação!";
        } else if (errorMessage?.toLowerCase().includes("500")) {
            msg = "Erro no servidor. Contate o suporte.";
        } else if (errorMessage?.toLowerCase().includes("503")) {
            msg = "Serviço de login indisponível.";
        } else if (errorMessage?.toLowerCase().includes("204")) {
            msg = "Usuário não encontrado.";
        } else if (errorMessage?.toLowerCase().includes("401")) {
            msg = "Usuário ou senha inválidos.";
        } else {
            msg = errorMessage || "Erro ao logar!";
        }

        if (msg) {
            enqueueSnackbar(msg, { variant: 'error' });
            handleError(msg);

            dispatch(logout());
            setTimeout(() => {
                auth.signoutRedirect();
            }, 2000);
        }
    }, [loginError, tokenError, errorMessage, enqueueSnackbar, handleError, dispatch, auth]);

    if (sessionReady && account && account?.name) {
        return null;
    }

    if (sessionStatus === 'resolving' || sessionStatus === 'initializing') {
        return (
            <Container maxWidth="xs" id="login-form" style={{ textAlign: "center" }}>
                <CircularProgress
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)'
                    }}
                />
            </Container>
        );
    }

    return (
        <Container maxWidth="xs" id="login-form" style={{
            textAlign: "center"
        }}>
            <CircularProgress 
                style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)'
                }} 
            />    
        </Container>
    );
};

export default Login;
